import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import type { ActivityFilter } from "../api.ts";
import styles from "../components/activity/Activity.module.css";
import { ActivityLog } from "../components/activity/ActivityLog.tsx";
import groups from "../components/groups/Groups.module.css";
import { Icon } from "../components/Icon.tsx";
import statePanel from "../components/StatePanel.module.css";
import { StatePanel } from "../components/StatePanel.tsx";
import ui from "../components/ui.module.css";
import { activityQuery, messageOf } from "../lib/queries.ts";

const FILTERS: {
  value: ActivityFilter | undefined;
  label: string;
  empty: string;
  fileEmpty: string;
}[] = [
  {
    value: undefined,
    label: "All",
    empty: "Requests appear here as they happen.",
    fileEmpty: "Nothing has happened to this file yet.",
  },
  {
    value: "tokens",
    label: "API tokens",
    empty: "No API token has made a request.",
    fileEmpty: "No API token has touched this file.",
  },
  {
    value: "shares",
    label: "Share links",
    empty: "No share link has been opened.",
    fileEmpty: "No share link has opened this file.",
  },
  {
    value: "people",
    label: "People",
    empty: "Nobody else has signed in to open a file.",
    fileEmpty: "Nobody else has signed in to open this file.",
  },
];

type ActivitySearch = { actor?: ActivityFilter; node?: string };

const isFilter = (value: unknown): value is ActivityFilter =>
  value === "tokens" || value === "shares" || value === "people";

export const Route = createFileRoute("/activity")({
  validateSearch: (raw: Record<string, unknown>): ActivitySearch => ({
    ...(isFilter(raw.actor) ? { actor: raw.actor } : {}),
    ...(typeof raw.node === "string" && raw.node ? { node: raw.node } : {}),
  }),
  loaderDeps: ({ search: { actor, node } }) => ({ actor, node }),
  loader: ({ context, deps: { actor, node } }) =>
    context.queryClient.ensureInfiniteQueryData(activityQuery(actor, node)),
  component: ActivityPage,
  pendingComponent: ActivityPending,
  errorComponent: ActivityError,
});

function ActivityPage() {
  const { actor, node } = Route.useSearch();
  const pages = useSuspenseInfiniteQuery(activityQuery(actor, node));
  const entries = pages.data.pages.flatMap((page) => page.items);
  const filter = FILTERS.find((option) => option.value === actor) ?? FILTERS[0];

  useEffect(() => {
    document.title = "Activity · Vault";
  }, []);

  return (
    <div className={styles.page}>
      <Intro actor={actor} node={node} />
      {entries.length === 0 ? (
        <div className={`${ui.card} ${groups.empty}`}>
          <span className={statePanel.icon} aria-hidden="true">
            <Icon name="pulse" />
          </span>
          <h2 className={groups.emptyTitle}>Nothing yet</h2>
          <p className={groups.emptyDetail}>{node ? filter?.fileEmpty : filter?.empty}</p>
        </div>
      ) : (
        <div className={`${ui.card} ${styles.sheet}`}>
          <ActivityLog entries={entries} />
        </div>
      )}
      {pages.hasNextPage ? (
        <div className={styles.more}>
          <button
            type="button"
            className={ui.button}
            onClick={() => {
              if (!pages.isFetchingNextPage) void pages.fetchNextPage();
            }}
            aria-disabled={pages.isFetchingNextPage || undefined}
          >
            {pages.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Intro({ pending = false, actor, node }: { pending?: boolean } & ActivitySearch) {
  return (
    <div className={styles.intro}>
      <div className={styles.heading}>
        <h1 className={styles.title}>Activity</h1>
        <p className={styles.note}>
          Every request in the last 45 days, except your own browsing in this app.
        </p>
        {node ? (
          <p className={styles.scope}>
            Showing one file.{" "}
            <Link to="/activity" search={actor ? { actor } : {}} className={styles.scopeLink}>
              Show everything
            </Link>
          </p>
        ) : null}
      </div>
      {pending ? (
        <span className={`${ui.skeleton} ${styles.filtersSkeleton}`} />
      ) : (
        <nav className={styles.filters} aria-label="Show requests by">
          {FILTERS.map((option) => (
            <Link
              key={option.label}
              to="/activity"
              search={{
                ...(option.value ? { actor: option.value } : {}),
                ...(node ? { node } : {}),
              }}
              className={styles.filter}
              activeOptions={{ exact: true }}
            >
              {option.label}
            </Link>
          ))}
        </nav>
      )}
    </div>
  );
}

function ActivityPending() {
  return (
    <div className={styles.page} aria-busy="true">
      <Intro pending />
      <div className={`${ui.card} ${styles.sheet}`}>
        <div className={styles.pendingRows}>
          <span className={`${ui.skeleton} ${styles.pendingDay}`} />
          {["first", "second", "third", "fourth", "fifth"].map((row) => (
            <div key={row} className={styles.pendingRow}>
              <span className={ui.skeleton} />
              <span className={ui.skeleton} />
              <span className={ui.skeleton} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActivityError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  return (
    <StatePanel icon="alert" title="Activity did not load" detail={messageOf(error)}>
      <button
        type="button"
        className={ui.button}
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
